import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { API_BASE_URL } from '../api-config';
import type { ApiCategory, ApiCategoryPage } from './catalog-api.models';
import type { Category } from './catalog.models';
function mapCategory(c: ApiCategory): Category {
  return {
    id: String(c.id),
    name: c.name,
    slug: c.slug,
    image: '',
    description: c.description ?? '',
    isActive: c.is_active,
    order: 0
  };
}

@Injectable({ providedIn: 'root' })
export class CategoryService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(API_BASE_URL);

  private readonly _cats = signal<Category[]>([]);

  readonly categories = this._cats.asReadonly();

  loadAll(elements = 100): void {
    this.http
      .get<ApiCategoryPage>(`${this.base}/categories`, {
        params: { page: '1', elements: String(elements) }
      })
      .subscribe({
        next: (res) => this._cats.set(res.data.map(mapCategory)),
        error: (err) => console.error('Error cargando categorías', err)
      });
  }

  getById(id: string): Category | undefined {
    return this._cats().find((c) => c.id === id);
  }

  getBySlug(slug: string): Category | undefined {
    return this._cats().find((c) => c.slug === slug);
  }

  slugExists(slug: string, exceptId?: string): boolean {
    return this._cats().some((c) => c.slug === slug && c.id !== exceptId);
  }

  upsert(cat: Category): void {
    this._cats.update((list) => {
      const idx = list.findIndex((c) => c.id === cat.id);
      return idx >= 0 ? list.map((c, i) => (i === idx ? cat : c)) : [...list, cat];
    });
  }

  delete(id: string): void {
    this._cats.update((list) => list.filter((c) => c.id !== id));
  }

  nextId(): string {
    const ids = this._cats()
      .map((c) => Number(c.id))
      .filter((n) => !isNaN(n));
    return String(ids.length ? Math.max(...ids) + 1 : 1);
  }

  /** Guarda (crea o actualiza) una categoría en el API y actualiza la señal local. */
  saveCategory(cat: Category, isNew: boolean): Observable<ApiCategory> {
    const payload = {
      name: cat.name,
      slug: cat.slug,
      description: cat.description || null,
      is_active: cat.isActive
    };

    const req$ = isNew
      ? this.http.post<ApiCategory>(`${this.base}/categories`, payload)
      : this.http.patch<ApiCategory>(`${this.base}/categories/${cat.id}`, payload);

    return req$.pipe(tap((saved) => this.upsert(mapCategory(saved))));
  }

  /** Elimina una categoría en el API y la quita de la señal local. */
  deleteCategory(id: string): Observable<unknown> {
    return this.http
      .delete(`${this.base}/categories/${id}`)
      .pipe(tap(() => this.delete(id)));
  }
}
